import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useLocalization from '../../../hooks/useLocalization';
import { usePay2Local } from '../../../hooks/payment/usePay2Local';
import { PAYMENT_TRANSACTION_TYPE } from '../../../types/enums';
import { FormTextField } from '../../molecules/form-elements';
import StyledForm from '../shared/StyledForm';
import { useMemo } from 'react';

const Pay2LocalWithdrawForm = ({ paymentProvider }: { paymentProvider: string | null }) => {
  const { t } = useLocalization();
  const {
    mutation: { mutate: withdraw, isPending },
  } = usePay2Local(PAYMENT_TRANSACTION_TYPE.WITHDRAW);

  const schema = useMemo(() => {
    return z.object({
      amount: z.coerce.number().min(1, t('minAmountError')),
      cust_phone: z
        .preprocess(
          (val) => {
            if (typeof val === 'string') {
              return val.replace(/\s+/g, '');
            }
            return val;
          },
          z.string({ error: 'requiredField' }).regex(/^(?:\+8801|8801|01)[3-9]\d{8}$/, t('invalidPhoneNumber'))
        )
        .optional(),
    });
  }, [t]);

  type FormInput = z.input<typeof schema>;
  type FormData = z.output<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput, unknown, FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: undefined,
      cust_phone: '',
    },
  });

  const onSubmit = (data: FormData) => {
    withdraw({
      ...data,
      currency: 'BDT',
      bank_type: paymentProvider,
      service_type: 'p2p_agent',
      redirect_url: window.location.origin,
    });
  };

  return (
    <StyledForm submitTitle={t('continue')} onSubmit={handleSubmit(onSubmit)} isSubmitting={isPending} disablePadding>
      <FormTextField
        name='amount'
        control={control}
        label={t('sum')}
        variant='outlined'
        fullWidth
        type='number'
        error={!!errors.amount}
        helperText={errors.amount?.message}
        disabled={isPending}
        required
        inputMode='numeric'
      />
      <FormTextField
        control={control}
        name='cust_phone'
        label={t('phoneNumber')}
        type='tel'
        placeholder='+88 01XXXXXXXXX'
        error={!!errors.cust_phone}
        helperText={errors.cust_phone?.message}
        disabled={isPending}
        required
        inputMode='numeric'
      />
    </StyledForm>
  );
};

export default Pay2LocalWithdrawForm;
